import React, { useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import paginationFactory, {
    PaginationListStandalone,
    PaginationProvider,
    PaginationTotalStandalone,
    SizePerPageDropdownStandalone
} from 'react-bootstrap-table2-paginator';
import Select from "react-select";
import RcTooltip from 'rc-tooltip';
import Modal from "react-bootstrap/Modal";
import Image from '../../components/image';
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import {
    Search,
    Blocks as LucideIntegration,
    Server as LucideServer,
    ChevronRight as LucideChevron,
    Database as LucideDatabase,
    Layers as LucideLayers
} from 'lucide-react';
import { dataRunStatuses, IN_PROGRESS_STATUSES } from '../../utils/constant';
import Load from '../../components/loadAction';

const IconIntegration = ({ size = 15, className = "tw-flex-none", ...props }) => (
    <LucideIntegration size={size} className={className} strokeWidth={2} {...props} />
);

const IconServer = ({ size = 12, className = "tw-flex-none", ...props }) => (
    <LucideServer size={size} className={className} strokeWidth={2} {...props} />
);

const IconChevron = ({ size = 11, className = "tw-flex-none", ...props }) => (
    <LucideChevron size={size} className={className} strokeWidth={2} {...props} />
);

const IconDatabase = ({ size = 12, className = "tw-flex-none", ...props }) => (
    <LucideDatabase size={size} className={className} strokeWidth={2} {...props} />
);

const IconLayers = ({ size = 14, className = "tw-flex-none", ...props }) => (
    <LucideLayers size={size} className={className} strokeWidth={2} {...props} />
);

const IconPartition = ({ size = 14, className = "tw-flex-none", color, ...props }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
        <path d="M15 6a9 9 0 0 0-9 9V3" />
        <circle cx="18" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
    </svg>
);

const IconFailedStatus = ({ size = 14, className = "tw-flex-none", color, strokeWidth = 2, ...props }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
        <path d="M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
    </svg>
);

const IconCircleExclamation = ({ size = 10, className = "tw-flex-none", ...props }) => (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
        <path d="M5 7.26392C5.14167 7.26392 5.26042 7.21751 5.35625 7.1247C5.45208 7.03188 5.5 6.91687 5.5 6.77966C5.5 6.64245 5.45208 6.52744 5.35625 6.43462C5.26042 6.34181 5.14167 6.2954 5 6.2954C4.85833 6.2954 4.73958 6.34181 4.64375 6.43462C4.54792 6.52744 4.5 6.64245 4.5 6.77966C4.5 6.91687 4.54792 7.03188 4.64375 7.1247C4.73958 7.21751 4.85833 7.26392 5 7.26392ZM5 5.32688C5.14167 5.32688 5.26042 5.28047 5.35625 5.18765C5.45208 5.09483 5.5 4.97982 5.5 4.84262V2.90557C5.5 2.76836 5.45208 2.65335 5.35625 2.56053C5.26042 2.46772 5.14167 2.42131 5 2.42131C4.85833 2.42131 4.73958 2.46772 4.64375 2.56053C4.54792 2.65335 4.5 2.76836 4.5 2.90557V4.84262C4.5 4.97982 4.54792 5.09483 4.64375 5.18765C4.73958 5.28047 4.85833 5.32688 5 5.32688ZM5 9.68523C4.30833 9.68523 3.65833 9.55811 3.05 9.30387C2.44167 9.04964 1.9125 8.7046 1.4625 8.26877C1.0125 7.83293 0.65625 7.32042 0.39375 6.73124C0.13125 6.14205 0 5.51251 0 4.84262C0 4.17272 0.13125 3.54318 0.39375 2.95399C0.65625 2.36481 1.0125 1.8523 1.4625 1.41646C1.9125 0.98063 2.44167 0.635593 3.05 0.381356C3.65833 0.127119 4.30833 0 5 0C5.69167 0 6.34167 0.127119 6.95 0.381356C7.55833 0.635593 8.0875 0.98063 8.5375 1.41646C8.9875 1.8523 9.34375 2.36481 9.60625 2.95399C9.86875 3.54318 10 4.17272 10 4.84262C10 5.51251 9.86875 6.14205 9.60625 6.73124C9.34375 7.32042 8.9875 7.83293 8.5375 8.26877C8.0875 8.7046 7.55833 9.04964 6.95 9.30387C6.34167 9.55811 5.69167 9.68523 5 9.68523Z" fill="#F64E60" />
    </svg>
);

const renderModalHeaderStatus = (status = 'Completed') => {
    if (status === 'Failed' || status === 'Error') {
        return (
            <span className="tw-px-2 tw-py-0.5 tw-rounded-md tw-bg-[#FEF3F2] dark:tw-bg-[#D92D20]/20 tw-border tw-border-[#FECDCA] dark:tw-border-[#D92D20] tw-text-[#D92D20] dark:tw-text-[#F04438] tw-text-xs tw-font-medium tw-inline-flex tw-items-center tw-gap-1">
                <IconFailedStatus size={12} />
                Failed
            </span>
        );
    }
    if (status === 'In Progress' || status === 'Processing' || status === 'Progress') {
        return (
            <span className="tw-px-2 tw-py-0.5 tw-rounded-md tw-bg-[#F4F8FF] dark:tw-bg-[#1570EF]/20 tw-border tw-border-[#B2DDFF] dark:tw-border-[#1570EF] tw-text-[#1570EF] dark:tw-text-[#53B1FD] tw-text-xs tw-font-medium tw-inline-flex tw-items-center tw-gap-1">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="tw-animate-spin">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                In Progress
            </span>
        );
    }
    return (
        <span className="tw-px-2 tw-py-0.5 tw-rounded-md tw-bg-[#ECFDF3] dark:tw-bg-[#027A48]/20 tw-border tw-border-[#ABE5C6] dark:tw-border-[#027A48] tw-text-[#027A48] dark:tw-text-[#32D583] tw-text-xs tw-font-medium tw-inline-flex tw-items-center tw-gap-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <path d="m16 9-5.5 5.5L8 12" />
            </svg>
            Completed
        </span>
    );
};

const renderModalStatusRow = (status = 'Completed', percentage = 100) => {
    let icon = null;
    let textColorClass = "tw-text-[#027A48] dark:tw-text-[#32D583]";
    let barBgClass = "tw-bg-[#17B26A]";
    let label = "Completed";

    if (status === 'Failed' || status === 'Error') {
        icon = <IconFailedStatus size={14} />;
        textColorClass = "tw-text-[#D92D20] dark:tw-text-[#F04438]";
        barBgClass = "tw-bg-[#F04438]";
        label = "Failed";
    } else if (status === 'In Progress' || status === 'Processing' || status === 'Progress') {
        icon = (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="tw-animate-spin tw-flex-none">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
        );
        textColorClass = "tw-text-[#1570EF] dark:tw-text-[#53B1FD]";
        barBgClass = "tw-bg-gradient-to-r tw-from-[#175cd3] tw-to-[#53b1fd]";
        label = "In Progress";
    } else {
        icon = (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="tw-flex-none">
                <circle cx="12" cy="12" r="10" />
                <path d="m16 9-5.5 5.5L8 12" />
            </svg>
        );
        textColorClass = "tw-text-[#027A48] dark:tw-text-[#32D583]";
        barBgClass = "tw-bg-[#17B26A]";
        label = "Completed";
    }

    const pct = percentage === undefined || percentage === null ? 100 : percentage;

    return (
        <div className="tw-w-full tw-max-w-[280px] tw-flex tw-flex-col tw-gap-1.5">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <span className={`${textColorClass} tw-text-[13px] tw-font-semibold tw-inline-flex tw-items-center tw-gap-1.5 tw-text-left`}>
                    {icon}
                    {label}
                </span>
                <span className="tw-text-xs tw-font-medium tw-text-[#484848] dark:tw-text-[#FFFFFF] [font-variant-numeric:tabular-nums]">
                    {pct}%
                </span>
            </div>
            <div className="tw-w-full tw-h-2 tw-rounded-full tw-overflow-hidden tw-bg-[#E9EAEB] dark:tw-bg-[#2B2B40]">
                <div
                    className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 tw-ease-in-out ${barBgClass}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
};

// Static JSON data matching profile-status-table.html reference exactly
// const STATIC_PROFILE_STATUS_DATA = [
//     {
//         id: 1,
//         integrationName: "adl_file_replace",
//         environment: "Production Environment Module",
//         dataSource: "Azure Data Lake",
//         summary: {
//             datasetsCount: 4,
//             errorsCount: 0,
//             errorsLabel: "No errors"
//         },
//         lastProfiledTime: {
//             relative: "21 days ago",
//             absolute: "12 Aug 2026, 15:31 GMT"
//         },
//         profileStatus: {
//             status: "Completed",
//             percentage: 100,
//             metaText: "All datasets refreshed"
//         },
//         datasets: {
//             summaryText: "4 datasets · 20 partitions",
//             list: [
//                 {
//                     name: "fact_manufacturing_materials",
//                     partitionBadge: "20",
//                     partitionTree: [
//                         { name: "StarDataset$City$Chennai", isTree: true },
//                         { name: "+19 more partitions", isMorePartitions: true }
//                     ]
//                 },
//                 { name: "maintenance_work_orders" },
//                 { name: "manufacturing_machine_downtime" }
//             ],
//             moreCount: 1
//         }
//     },
//     {
//         id: 2,
//         integrationName: "all_schema_check__adl",
//         environment: "Staging",
//         dataSource: "Azure Data Lake",
//         summary: {
//             datasetsCount: 3,
//             errorsCount: 0,
//             errorsLabel: "No errors"
//         },
//         lastProfiledTime: {
//             relative: "Started 18m ago",
//             absolute: "12 Aug 2026, 11:40 GMT"
//         },
//         profileStatus: {
//             status: "Processing",
//             percentage: 60,
//             metaText: "Refresh under way · running 18m"
//         },
//         datasets: {
//             summaryText: "3 datasets",
//             list: [
//                 { name: "accuracy_age_fever_v2" },
//                 { name: "attribute_null_test" },
//                 { name: "behrain_test_data" }
//             ],
//             moreCount: 0
//         }
//     },
//     {
//         id: 3,
//         integrationName: "manufacturing_dq",
//         environment: "Production",
//         dataSource: "Snowflake Enterprise Warehouse",
//         summary: {
//             datasetsCount: 6,
//             errorsCount: 2,
//             errorsLabel: "2 errors"
//         },
//         lastProfiledTime: {
//             relative: "21 days ago",
//             absolute: "12 Aug 2026, 09:14 GMT"
//         },
//         profileStatus: {
//             status: "Failed",
//             percentage: 45,
//             metaText: "Some datasets could not be refreshed"
//         },
//         datasets: {
//             summaryText: "6 datasets",
//             list: [
//                 {
//                     name: "order_list_invoice",
//                     isError: true,
//                     status: "Failed",
//                     errorMessage: "Error while processing the configured dataset. Could be due to unexpected/invalid values in the dataset or incorrect format of the dataset",
//                     lastProcessingTime: "Wed, 12 Aug 2026 09:14:05 GMT",
//                     successfulRefresh: 0,
//                     failedRefresh: 1,
//                     errorsCount: 2
//                 },
//                 {
//                     name: "plant_utilisation",
//                     isError: true,
//                     status: "Failed",
//                     errorMessage: "Error while processing the configured dataset. Could be due to unexpected/invalid values in the dataset or incorrect format of the dataset",
//                     lastProcessingTime: "Wed, 12 Aug 2026 09:14:05 GMT",
//                     successfulRefresh: 0,
//                     failedRefresh: 1,
//                     errorsCount: 2
//                 },
//                 { name: "manufacturing_machine_downtime", status: "Completed" }
//             ],
//             moreCount: 3
//         }
//     },
//     {
//         id: 4,
//         integrationName: "manualadl",
//         environment: "Development",
//         dataSource: "Azure Data Lake",
//         summary: {
//             datasetsCount: 2,
//             errorsCount: 0,
//             errorsLabel: "No errors"
//         },
//         lastProfiledTime: {
//             relative: "—",
//             absolute: ""
//         },
//         profileStatus: {
//             status: "Not yet started",
//             percentage: 0,
//             metaText: "Waiting for its first run"
//         },
//         datasets: {
//             summaryText: "2 datasets",
//             list: [
//                 { name: "adl_test1" },
//                 { name: "adl_test2" }
//             ],
//             moreCount: 0
//         }
//     },
//     {
//         id: 5,
//         integrationName: "empty_dataset_check",
//         environment: "Production",
//         dataSource: "Amazon S3",
//         summary: {
//             datasetsCount: 0,
//             errorsCount: 1,
//             errorsLabel: "No datasets"
//         },
//         lastProfiledTime: {
//             relative: "2 hours ago",
//             absolute: "7 Sep 2026, 09:30 GMT"
//         },
//         profileStatus: {
//             status: "No Datasets Found Yet",
//             percentage: 100,
//             metaText: "Latest scan completed · 0 datasets discovered"
//         },
//         datasets: {
//             summaryText: "0 datasets",
//             list: [],
//             moreCount: 0
//         }
//     }
// ];

// // Static datasets and partitions list matching reference drawer screenshots
// const MODAL_PARTITIONS = [
//     { name: "StarDataset$City$Chennai", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Mumbai", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Hyderabad", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Pune", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Kochi", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Delhi", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Bengaluru", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Kolkata", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Jaipur", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Surat", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Indore", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Nagpur", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Bhopal", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Patna", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Lucknow", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Kanpur", status: "Failed", isPartition: true },
//     { name: "StarDataset$City$Nashik", status: "Completed", isPartition: true },
//     { name: "StarDataset$City$Vadodara", status: "Completed", isPartition: true }
// ];

// const MODAL_DATASETS = [
//     {
//         name: "fact_manufacturing_materials",
//         partitionCount: 20,
//         status: "Completed",
//         hasPartitions: true,
//         partitions: MODAL_PARTITIONS
//     },
//     {
//         name: "maintenance_work_orders",
//         status: "Completed"
//     },
//     {
//         name: "manufacturing_machine_downtime",
//         status: "Completed"
//     },
//     {
//         name: "order_list_invoice",
//         status: "Failed",
//         isError: true,
//         errorMessage: "Error while processing the configured dataset. Could be due to unexpected/invalid values in the dataset or incorrect format of the dataset",
//         lastProcessingTime: "Wed, 12 Aug 2026 09:14:05 GMT",
//         successfulRefresh: 0,
//         failedRefresh: 1,
//         errorsCount: 2
//     },
//     {
//         name: "plant_utilisation",
//         status: "Failed",
//         isError: true,
//         errorMessage: "Error while processing the configured dataset. Could be due to unexpected/invalid values in the dataset or incorrect format of the dataset",
//         lastProcessingTime: "Wed, 12 Aug 2026 09:14:05 GMT",
//         successfulRefresh: 0,
//         failedRefresh: 1,
//         errorsCount: 2
//     }
// ];

const asArray = (value) => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== 'object') return [];
    if (Array.isArray(value.data)) return value.data;
    if (Array.isArray(value.response)) return value.response;
    if (Array.isArray(value.final)) return value.final;
    if (Array.isArray(value.results)) return value.results;
    if (Array.isArray(value.items)) return value.items;
    return [];
};

const sanitizeRefreshRows = (rows) => asArray(rows)
    .filter(Boolean)
    .map((row, index) => {
        if (!row || typeof row !== 'object') {
            return null;
        }

        return {
            ...row,
            id: row.id ?? index + 1,
            datasets: {
                ...(row.datasets || {}),
                list: asArray(row?.datasets?.list).filter(Boolean)
            }
        };
    })
    .filter(Boolean);

const renderNoDataPlaceholder = (message = "No Data Found") => (
    <div className="row text-center" style={{ width: "100%", padding: "40px 0" }}>
        <div className="col-md-8 offset-md-2 col-lg-6 offset-lg-3">
            <div className="placeholder-msg">
                <div className="placeholder-msg__icon">
                    <Image src="icon_placeholder_no_pipeline_data" />
                </div>
                <p>{message}</p>
            </div>
        </div>
    </div>
);

const relativeTime = (value) => {
    const timestamp = value && new Date(value);
    if (!timestamp || Number.isNaN(timestamp.getTime())) return '—';
    const seconds = Math.max(0, Math.floor((Date.now() - timestamp.getTime()) / 1000));
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
};

// Combines data_refreshes_info with integration and datasource metadata.
const buildRefreshRows = (dataRefreshes, integrations, datasources) => {
    const integrationList = asArray(integrations);
    const datasourceList = asArray(datasources);
    const normalizedRefreshes = asArray(dataRefreshes);
    const findDatasource = (name) => datasourceList.find(datasource =>
        asArray(datasource.datasource_config?.integration_names).some(item => item.integration_name === name)
    );

    return normalizedRefreshes.map((refresh, index) => {
        const integrationName = refresh.integration_name || `Integration ${index + 1}`;
        const datasource = findDatasource(integrationName);
        const integration = integrationList.find(item => item.integration_name === integrationName) || {};
        const processing = asArray(refresh.dataset_processing_info);
        const progressByDatasetId = new Map(
            asArray(refresh.progress_info).map(progress => [progress.data_set_id, progress])
        );
        // Some API responses include successful datasets in dataset_failed_refresh.
        // Treat a dataset as failed only when its flag or status actually says so.
        const failedIds = new Set(asArray(refresh.dataset_failed_refresh)
            .filter(dataset => Number(dataset.failed) > 0 || /fail|error/i.test(dataset.run_status_text || ''))
            .map(dataset => dataset.data_set_id));
        const successIds = new Set(asArray(refresh.dataset_success_refresh).map(dataset => dataset.data_set_id));
        const processedDatasets = processing.map(dataset => {
            const failed = failedIds.has(dataset.data_set_id) || Number(dataset.failed) > 0 || Number(dataset.run_status_code) < 0 || /fail|error/i.test(dataset.run_status_text || '');
            const completed = Number(dataset.run_status_code) === 1 || /success|complete/i.test(dataset.run_status_text || '') || successIds.has(dataset.data_set_id);
            const status = failed ? 'Failed' : (completed ? 'Completed' : 'Processing');
            const progressInfo = progressByDatasetId.get(dataset.data_set_id) || dataset.progress_info;
            const progressFields = [
                'cache_view',
                'data_profiling',
                'datatype_identification',
                'metrics_computation',
                'model_performance',
                'relationship_identification'
            ];
            let progressInfoTotal = 40;
            if (progressInfo) {
                progressInfoTotal = progressFields.reduce(
                    (total, field) => total + Number(progressInfo[field] || 0),
                    0
                );
            }
            let progressPercentage = 100;
            if (IN_PROGRESS_STATUSES.includes(Number(dataset.run_status_code))) {
                progressPercentage = Math.min(75, ((progressInfoTotal / 40) * 100) / 2 + 25);
            } else if (Number(dataset.run_status_code) === dataRunStatuses.inQueue) {
                progressPercentage = 25;
            } else if (Number(dataset.run_status_code) === dataRunStatuses.skipMcf) {
                progressPercentage = 0;
            }
            const partitions = asArray(dataset.partitions || dataset.partition_processing_info || dataset.partition_info)
                .map(partition => ({
                    id: partition.partition_id || partition.id || partition.name,
                    name: partition.partition_name || partition.name || partition.data_set_name || 'Unnamed partition',
                    status: Number(partition.failed) > 0 || /fail|error/i.test(partition.run_status_text || partition.status || '') ? 'Failed' : 'Completed'
                }));
            return {
                id: dataset.data_set_id,
                name: dataset.data_set_name || `Dataset ${dataset.data_set_id}`,
                status,
                progressPercentage,
                isError: failed,
                errorMessage: dataset.error_message || dataset.error || '',
                lastProcessingTime: dataset.run_end_time || dataset.run_started_time || '',
                successfulRefresh: Number(dataset.success || successIds.has(dataset.data_set_id)),
                failedRefresh: Number(dataset.failed || failed),
                errorsCount: failed ? 1 : 0,
                hasPartitions: partitions.length > 0,
                partitionCount: partitions.length,
                partitions,
                partitionTree: partitions.map(partition => ({ name: partition.name, status: partition.status }))
            };
        });
        // Qualdo creates partition datasets using the _qldds delimiter. Group those
        // records beneath their parent dataset instead of rendering them as datasets.
        const datasets = processedDatasets.filter(dataset => !dataset.name.includes('_qldds'));
        processedDatasets.filter(dataset => dataset.name.includes('_qldds')).forEach(partition => {
            const parent = datasets.find(dataset => dataset.name === partition.name.split('_qldds')[0]);
            if (parent) {
                parent.partitions = [...parent.partitions, { ...partition, isPartition: true, parent: parent.name }];
                parent.partitionTree = parent.partitions.map(item => ({ name: item.name, status: item.status }));
                parent.partitionCount = parent.partitions.length;
                parent.hasPartitions = true;
                if (partition.status === 'Failed') {
                    parent.status = 'Failed';
                    parent.isError = true;
                    parent.failedRefresh = 1;
                    parent.errorsCount = Math.max(parent.errorsCount, 1);
                }
            }
        });
        const failedCount = datasets.filter(dataset => dataset.status === 'Failed').length;
        const refreshHasError = /fail|error|incorrect credentials/i.test(refresh.status_text || '');
        const latestRun = processing.reduce((latest, dataset) => {
            const current = dataset.run_end_time || dataset.run_started_time;
            return !latest || (current && new Date(current) > new Date(latest)) ? current : latest;
        }, '');
        const hasProcessingDatasets = datasets.some(dataset => dataset.status === 'Processing');
        const status = failedCount || refreshHasError ? 'Failed' :
            (hasProcessingDatasets ? 'Processing' :
                (datasets.length && datasets.every(dataset => dataset.status === 'Completed') ? 'Completed' : 'Not yet started'));
        const totalErrorCount = failedCount || (refreshHasError ? 1 : 0);
        const progressPercentage = datasets.length
            ? Math.round(datasets.reduce((total, dataset) => total + dataset.progressPercentage, 0) / datasets.length)
            : 0;

        return {
            // The table ID is a display sequence, not the API integration ID.
            id: index + 1,
            integrationName,
            environment: datasource?.env_name || integration.env_name || integration.environment_name || '—',
            dataSource: datasource?.datasource_name || integration.datasource_name || '—',
            summary: {
                datasetsCount: datasets.length,
                errorsCount: totalErrorCount,
                errorsLabel: totalErrorCount ? `${totalErrorCount} error${totalErrorCount === 1 ? '' : 's'}` : 'No errors'
            },
            lastProfiledTime: { relative: relativeTime(latestRun), absolute: latestRun || '' },
            profileStatus: {
                status,
                percentage: progressPercentage,
                metaText: refresh.status_text || ''
            },
            datasets: {
                summaryText: `${datasets.length} dataset${datasets.length === 1 ? '' : 's'}`,
                list: datasets,
                moreCount: 0
            }
        };
    });
};

// Helper to truncate text to 15 characters
const truncate15 = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str.length > 15 ? str.substring(0, 15) + '...' : str;
};

// Helper to truncate text to 25 characters
const truncate25 = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str.length > 25 ? str.substring(0, 25) + '...' : str;
};

// Exact cell formatters matching reference file design and typography
const idFormatter = (cell) => (
    <span className="tw-text-[#9b9b9b] dark:tw-text-[#7E8299] tw-font-normal tw-text-sm">{cell}</span>
);

const integrationNameFormatter = (cell, row, rowIndex, formatExtraData) => {
    const isEnvExceeded = row.environment && row.environment.length > 15;
    const isDsExceeded = row.dataSource && row.dataSource.length > 15;
    const isExceeded = isEnvExceeded || isDsExceeded;

    const displayEnv = truncate15(row.environment);
    const displayDs = truncate15(row.dataSource);

    const breadcrumbContent = (
        <div className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-mt-1 tw-max-w-full ${isExceeded ? 'tw-cursor-pointer' : 'tw-cursor-default'}`}>
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-normal tw-text-[#767676] dark:tw-text-[#A1A5B7]">
                <IconServer />
                <span>{displayEnv}</span>
            </span>
            <IconChevron />
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-normal tw-text-[#767676] dark:tw-text-[#A1A5B7]">
                <IconDatabase />
                <span>{displayDs}</span>
            </span>
        </div>
    );

    return (
        <div className="tw-flex tw-items-start tw-gap-2.5 tw-min-w-0">
            <IconIntegration className="tw-flex-none tw-mt-0.5" />
            <div className="tw-min-w-0 tw-flex-1">
                <span
                    className="tw-block tw-text-sm tw-font-semibold tw-text-[#101828] dark:tw-text-[#FFFFFF] tw-truncate"
                >
                    {row.integrationName}
                </span>
                {isExceeded ? (
                    <RcTooltip
                        placement="top"
                        overlay={
                            <div className="tw-text-xs tw-p-1.5">
                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-0.5">
                                    <IconServer />
                                    <span>{row.environment}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-1.5">
                                    <IconDatabase />
                                    <span>{row.dataSource}</span>
                                </div>
                            </div>
                        }
                        trigger={["hover"]}
                        arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                    >
                        {breadcrumbContent}
                    </RcTooltip>
                ) : (
                    breadcrumbContent
                )}
            </div>
        </div>
    );
};

const summaryFormatter = (cell, row) => (
    <div>
        <span className="tw-block tw-text-sm tw-font-normal tw-text-[#9b9b9b] dark:tw-text-[#7E8299] tw-mb-0.5">
            No. Of Datasets
        </span>
        <div className="tw-flex tw-items-center tw-gap-2">
            <IconLayers color="#9b9b9b" />
            <span className="tw-text-lg tw-font-semibold tw-text-[#484848] dark:tw-text-[#FFFFFF] tw-leading-tight">
                {row.summary.datasetsCount}
            </span>
        </div>
        {row.summary.errorsCount > 0 ? (
            <span className="tw-block tw-mt-1.5 tw-text-sm tw-font-semibold tw-text-[#D92D20] dark:tw-text-[#F04438]">
                {row.summary.errorsLabel}
            </span>
        ) : (
            <span className="tw-block tw-mt-1.5 tw-text-sm tw-font-normal tw-text-[#9b9b9b] dark:tw-text-[#7E8299]">
                {row.summary.errorsLabel}
            </span>
        )}
    </div>
);

const timeFormatter = (cell, row) => (
    <div>
        <span className={`tw-block tw-text-sm tw-font-medium ${row.lastProfiledTime.relative === '—' ? 'tw-text-[#9b9b9b] dark:tw-text-[#7E8299]' : 'tw-text-[#484848] dark:tw-text-[#FFFFFF]'}`}>
            {row.lastProfiledTime.relative}
        </span>
        {row.lastProfiledTime.absolute ? (
            <span className="tw-block tw-text-xs tw-font-normal tw-text-[#9b9b9b] dark:tw-text-[#7E8299] tw-mt-0.5">
                {row.lastProfiledTime.absolute}
            </span>
        ) : null}
    </div>
);

const statusFormatter = (cell, row) => {
    const { status, percentage, metaText } = row.profileStatus;

    let icon = null;
    let textColorClass = "tw-text-[#667085]";
    let barBgClass = "tw-bg-transparent";

    if (status === "Completed") {
        icon = (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#17B26A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-flex-none">
                <circle cx="12" cy="12" r="10" />
                <path d="m16 9-5.5 5.5L8 12" />
            </svg>
        );
        textColorClass = "tw-text-[#17B26A]";
        barBgClass = "tw-bg-[#17B26A]";
    } else if (status === "Processing") {
        icon = (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#1570ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-animate-spin tw-flex-none">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
        );
        textColorClass = "tw-text-[#1570ef]";
        barBgClass = "tw-bg-gradient-to-r tw-from-[#175cd3] tw-to-[#53b1fd]";
    } else if (status === "Failed" || status === "No Datasets Found Yet") {
        icon = (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#F04438" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-flex-none">
                <path d="M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z" />
                <path d="M12 8v4" />
                <path d="M12 16h.01" />
            </svg>
        );
        textColorClass = "tw-text-[#F04438]";
        barBgClass = "tw-bg-[#F04438]";
    } else if (status === "Not yet started") {
        icon = (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-flex-none">
                <path d="M10.1 2.182a10 10 0 0 1 3.8 0" />
                <path d="M13.9 21.818a10 10 0 0 1-3.8 0" />
                <path d="M17.609 3.721a10 10 0 0 1 2.69 2.7" />
                <path d="M2.182 13.9a10 10 0 0 1 0-3.8" />
                <path d="M20.279 17.609a10 10 0 0 1-2.7 2.69" />
                <path d="M21.818 10.1a10 10 0 0 1 0 3.8" />
                <path d="M3.721 6.391a10 10 0 0 1 2.7-2.69" />
                <path d="M6.391 20.279a10 10 0 0 1-2.69-2.7" />
            </svg>
        );
        textColorClass = "tw-text-[#667085]";
        barBgClass = "tw-bg-transparent";
    }

    return (
        <div className="tw-w-[280px] tw-flex tw-flex-col tw-gap-1.5 tw-py-1">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
                    {icon}
                    <span className={`tw-text-sm tw-font-semibold tw-truncate ${textColorClass}`}>{status}</span>
                </div>
                <span className="tw-text-sm tw-font-medium tw-text-[#484848] dark:tw-text-[#FFFFFF] tw-shrink-0 [font-variant-numeric:tabular-nums]">
                    {percentage}%
                </span>
            </div>
            <div className="tw-w-full tw-h-2 tw-rounded-full tw-overflow-hidden tw-bg-[#E9EAEB] dark:tw-bg-[#2B2B40]">
                <div
                    className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 tw-ease-in-out ${barBgClass}`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
            <div className="tw-text-xs tw-font-normal tw-text-[#9b9b9b] dark:tw-text-[#7E8299] tw-leading-normal">{metaText}</div>
        </div>
    );
};

const datasetsFormatter = (cell, row, rowIndex, formatExtraData) => {
    const { summaryText, list, moreCount } = row.datasets;
    const visibleDatasets = list.slice(0, 3);
    const remainingDatasets = Math.max(list.length - visibleDatasets.length, moreCount || 0);

    return (
        <div className="tbl-partition-tree">
            <span className="tbl-partition-tree__head">{summaryText}</span>
            <ul className="tbl-partition-tree__list">
                {visibleDatasets.map((ds, idx) => {
                    const isDsExceeded = ds.name && ds.name.length > 25;
                    const displayDsName = truncate25(ds.name);
                    const visiblePartitionTree = ds.partitionTree?.slice(0, 1);
                    const remainingPartitions = Math.max((ds.partitionTree?.length || 0) - (visiblePartitionTree?.length || 0), 0);

                    const dsButtonContent = (
                        <button
                            type="button"
                            className={`tbl-partition-tree__item ${ds.isError ? "tbl-partition-tree__item--err" : ""}`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (formatExtraData && formatExtraData.onOpenModal) {
                                    formatExtraData.onOpenModal(row, ds, 'dataset_detail');
                                }
                            }}
                        >
                            <svg className="tbl-partition-tree__ico" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
                                <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
                                <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
                            </svg>
                            <span className="tbl-partition-tree__name">{displayDsName}</span>
                            {ds.isError && (
                                <IconCircleExclamation size={10} className="tw-flex-none" />
                            )}
                            {ds.partitionBadge && (
                                <span className="tbl-partition-tree__badge">
                                    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M15 6a9 9 0 0 0-9 9V3" />
                                        <circle cx="18" cy="6" r="3" />
                                        <circle cx="6" cy="18" r="3" />
                                    </svg>
                                    {ds.partitionBadge}
                                </span>
                            )}
                        </button>
                    );

                    return (
                        <li key={idx}>
                            {ds.isError ? (
                                <RcTooltip
                                    placement="top"
                                    overlay={
                                        <div className="tw-text-xs tw-p-1.5 tw-max-w-[280px] tw-leading-relaxed">
                                            {ds.errorMessage || "Error while processing the configured dataset. Could be due to unexpected/invalid values in the dataset or incorrect format of the dataset"}
                                        </div>
                                    }
                                    trigger={["hover"]}
                                    arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                                >
                                    {dsButtonContent}
                                </RcTooltip>
                            ) : isDsExceeded ? (
                                <RcTooltip
                                    placement="top"
                                    overlay={
                                        <div className="tw-text-xs tw-p-1.5 tw-flex tw-items-center tw-gap-1.5">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-flex-none">
                                                <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
                                                <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
                                                <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
                                            </svg>
                                            <span>{ds.name}</span>
                                        </div>
                                    }
                                    trigger={["hover"]}
                                    arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                                >
                                    {dsButtonContent}
                                </RcTooltip>
                            ) : (
                                dsButtonContent
                            )}
                            {ds.partitionTree && (
                                <ul className="tbl-partition-tree__kids">
                                    {visiblePartitionTree.map((pt, pIdx) => {
                                        if (pt.isMorePartitions) {
                                            return (
                                                <li key={pIdx} className="tbl-partition-tree__kids-more">
                                                    <button
                                                        type="button"
                                                        className="tbl-partition-tree__more tbl-partition-tree__more--child"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (formatExtraData && formatExtraData.onOpenModal) {
                                                                formatExtraData.onOpenModal(row, ds, 'integration');
                                                            }
                                                        }}
                                                    >
                                                        {pt.name}
                                                    </button>
                                                </li>
                                            );
                                        }

                                        const isPtExceeded = pt.name && pt.name.length > 25;
                                        const displayPtName = truncate25(pt.name);

                                        const ptButtonContent = (
                                            <button
                                                type="button"
                                                className="tbl-partition-tree__item tbl-partition-tree__item--child"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (formatExtraData && formatExtraData.onOpenModal) {
                                                        formatExtraData.onOpenModal(row, { ...pt, parent: ds.name, isPartition: true }, 'dataset_detail');
                                                    }
                                                }}
                                            >
                                                <svg className="tbl-partition-tree__ico" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M15 6a9 9 0 0 0-9 9V3" />
                                                    <circle cx="18" cy="6" r="3" />
                                                    <circle cx="6" cy="18" r="3" />
                                                </svg>
                                                <span className="tbl-partition-tree__name">{displayPtName}</span>
                                            </button>
                                        );

                                        return (
                                            <li key={pIdx} className="tbl-partition-tree__kids-last">
                                                {isPtExceeded ? (
                                                    <RcTooltip
                                                        placement="top"
                                                        overlay={
                                                            <div className="tw-text-xs tw-p-1.5 tw-flex tw-items-center tw-gap-1.5">
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tw-flex-none">
                                                                    <path d="M15 6a9 9 0 0 0-9 9V3" />
                                                                    <circle cx="18" cy="6" r="3" />
                                                                    <circle cx="6" cy="18" r="3" />
                                                                </svg>
                                                                <span>{pt.name}</span>
                                                            </div>
                                                        }
                                                        trigger={["hover"]}
                                                        arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
                                                    >
                                                        {ptButtonContent}
                                                    </RcTooltip>
                                                ) : (
                                                    ptButtonContent
                                                )}
                                            </li>
                                        );
                                    })}
                                    {remainingPartitions > 0 && (
                                        <li className="tbl-partition-tree__kids-more">
                                            <button
                                                type="button"
                                                className="tbl-partition-tree__more tbl-partition-tree__more--child"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (formatExtraData && formatExtraData.onOpenModal) {
                                                        formatExtraData.onOpenModal(row, ds, 'integration');
                                                    }
                                                }}
                                            >
                                                +{remainingPartitions} more
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
            {remainingDatasets > 0 && (
                <button
                    type="button"
                    className="tbl-partition-tree__more"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (formatExtraData && formatExtraData.onOpenModal) {
                            formatExtraData.onOpenModal(row, null, 'integration');
                        }
                    }}
                >
                    +{remainingDatasets} more
                </button>
            )}
        </div>
    );
};

const ConfigureDataRefreshTable = ({ dataRefreshes = [], integrations = [], datasources = [], isLoading = false }) => {
    const refreshRows = useMemo(() => {
        const normalized = sanitizeRefreshRows(buildRefreshRows(dataRefreshes, integrations, datasources));
        return Array.isArray(normalized) ? normalized.filter(Boolean) : [];
    }, [dataRefreshes, integrations, datasources]);

    const [showModal, setShowModal] = React.useState(false);
    const [modalView, setModalView] = React.useState('integration'); // 'integration' | 'dataset_detail'
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [selectedDataset, setSelectedDataset] = React.useState(null);
    const [expandedDataset, setExpandedDataset] = React.useState(null);
    const [modalFilterText, setModalFilterText] = React.useState('');
    const [searchText, setSearchText] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [currentSizePerPage, setCurrentSizePerPage] = React.useState(5);

    const filteredRefreshRows = useMemo(() => {
        const rows = Array.isArray(refreshRows) ? refreshRows.filter(Boolean) : [];
        const query = searchText.trim().toLowerCase();
        if (!query) return rows;
        return rows.filter(row => [
            row.integrationName,
            row.environment,
            row.dataSource,
            ...asArray(row?.datasets?.list).map(dataset => dataset?.name)
        ].some(value => String(value || '').toLowerCase().includes(query)));
    }, [refreshRows, searchText]);


    const modalDatasets = asArray(selectedRow?.datasets?.list).filter(dataset =>
        String(dataset?.name || '').toLowerCase().includes(modalFilterText.toLowerCase())
    );
    const visibleModalDatasets = modalDatasets;
    const hasFailureSummary = selectedRow?.profileStatus?.status === 'Failed' || (selectedRow?.summary?.errorsCount || 0) > 0;

    const handleOpenModal = (row, dataset = null, view = 'integration') => {
        setSelectedRow(row || null);
        setSelectedDataset(dataset);
        setModalView(view);
        setShowModal(true);
    };

    const tableColumns = [
        {
            dataField: 'id',
            text: 'ID',
            sort: true,
            formatter: idFormatter
        },
        {
            dataField: 'integrationName',
            text: 'Integration Name',
            sort: true,
            formatter: integrationNameFormatter,
            formatExtraData: { onOpenModal: handleOpenModal },
            filterValue: (cell, row) => `${row.integrationName} ${row.environment} ${row.dataSource}`
        },
        {
            dataField: 'summary',
            text: 'Summary',
            sort: false,
            formatter: summaryFormatter
        },
        {
            dataField: 'lastProfiledTime',
            text: 'Last Profiled Time',
            sort: true,
            formatter: timeFormatter,
            filterValue: (cell, row) => `${row.lastProfiledTime.relative} ${row.lastProfiledTime.absolute}`
        },
        {
            dataField: 'profileStatus',
            text: 'Profile Status',
            sort: false,
            formatter: statusFormatter,
            filterValue: (cell, row) => row.profileStatus.status
        },
        {
            dataField: 'datasets',
            text: 'Datasets',
            sort: false,
            formatter: datasetsFormatter,
            formatExtraData: {
                onOpenModal: handleOpenModal
            },
            filterValue: (cell, row) => row.datasets.list.map(d => d.name).join(" ")
        }
    ];

    const customTotal = (from, to, size) => (
        <span className="react-table__info">
            Showing <b>{from}</b> to <b>{to}</b> of <b>{size}</b> results
        </span>
    );


    const sizePerPageRenderer = ({
        options,
        currSizePerPage,
        onSizePerPageChange
    }) => (
        <div className="react-bootstrap-table__header">
            <div className="row">
                <div className="col-6 col-md-6">
                    <div className="btn-group react-table__length" role="group">
                        <span>Show</span>
                        <Select
                            classNamePrefix="select-control"
                            defaultValue={{ label: currSizePerPage, value: currSizePerPage }}
                            options={options.map((option) => ({ label: option.text, value: option.value ?? option.page }))}
                            value={{ label: currSizePerPage, value: currSizePerPage }}
                            onChange={(selectedOption) => {
                                onSizePerPageChange(selectedOption?.value || currSizePerPage);
                            }}
                        />
                        <span>items</span>
                    </div>
                </div>
                <div className="col-6 col-md-6">
                    <div className="btn-group react-table__filter" role="group">
                        <div className="table-search">
                            <input
                                placeholder="Search"
                                id="searchBox"
                                className="form-control !tw-bg-white"
                                value={searchText}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setSearchText(value);
                                    setCurrentPage(1);
                                }}
                                type="text"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const paginationOptions = {
        custom: true,
        page: currentPage,
        totalSize: filteredRefreshRows.length,
        sizePerPage: currentSizePerPage,
        onPageChange: (page) => setCurrentPage(page),
        onSizePerPageChange: (sizePerPage, page) => {
            setCurrentSizePerPage(sizePerPage);
            setCurrentPage(page || 1);
        },
        sizePerPageList: [
            { text: '5', value: 5 },
            { text: '10', value: 10 },
            { text: '25', value: 25 },
            { text: 'All', value: Math.max(filteredRefreshRows.length, 1) }
        ],
        paginationTotalRenderer: customTotal,
        sizePerPageRenderer: sizePerPageRenderer
    };

    const renderTableLoader = () => (
        <div className="portlet">
            <div className="portlet-title">
                <div className="caption">
                    <span className="text-uppercase font-dark">Processing Errors &amp; Data Refresh Status</span>
                </div>
            </div>
            <div className="portlet-body" aria-live="polite">
                <Load />
            </div>
        </div>
    );

    return (
        <div id="jump-to_processerror" className="tw-mt-6">
            <div className="portlet">
                <div className="portlet-title">
                    <div className="caption">
                        <span className="text-uppercase font-dark">Processing Errors &amp; Data Refresh Status</span>
                    </div>
                    <div className="actions d-flex"></div>
                </div>
                <div className="portlet-body">
                    {isLoading ? (
                        renderTableLoader()
                    ) : (
                        <PaginationProvider pagination={paginationFactory(paginationOptions)}>
                            {({ paginationProps, paginationTableProps }) => (
                                <div>
                                    <SizePerPageDropdownStandalone {...paginationProps} />
                                    <ToolkitProvider
                                        keyField="id"
                                        data={filteredRefreshRows}
                                        columns={tableColumns}
                                    >
                                        {(toolkitprops) => (
                                            <>
                                                <BootstrapTable
                                                    bootstrap4
                                                    bordered={false}
                                                    responsive
                                                    {...toolkitprops.baseProps}
                                                    {...paginationTableProps}
                                                    wrapperClasses="table-responsive"
                                                    tableHeaderClass="qd-table mb-0"
                                                    tableBodyClass="qd-table mb-0"
                                                    noDataIndication={() => (
                                                        searchText.trim()
                                                            ? renderNoDataPlaceholder("No Data Found")
                                                            : null
                                                    )}
                                                />
                                            </>
                                        )}
                                    </ToolkitProvider>
                                    <div className="react-bootstrap-table__footer">
                                        <div className="row">
                                            <div className="col-sm-12 col-md-6">
                                                <PaginationTotalStandalone {...paginationProps} />
                                            </div>
                                            <div className="col-sm-12 col-md-6">
                                                <PaginationListStandalone {...paginationProps} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </PaginationProvider>
                    )}
                </div>
            </div>

            {/* Right Slider Modal Drawer */}
            <Modal
                show={showModal}
                dialogClassName="modal-vertical shadow"
                className="fixed-right model__help modal-integration"
                size="xl"
                onHide={() => setShowModal(false)}
                centered
            >
                {modalView === 'integration' ? (
                    <>
                        <Modal.Header closeButton >
                            <div className='tw-flex tw-items-center tw-gap-3.5'>
                                <div className="tw-flex tw-items-center tw-justify-center tw-p-[10px] tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/20 tw-bg-white dark:tw-bg-white/10 tw-rounded-[10px]">
                                    <IconIntegration className="tw-text-[#414651] dark:tw-text-white" size={20} />
                                </div>
                                <div>
                                    <h5 className="modal-title !tw-m-0 !tw-mb-0.5 tw-text-[15px] tw-font-semibold !tw-text-[#101828] dark:!tw-text-[#FFFFFF]">
                                        {selectedRow?.integrationName || 'adl_file_replace'}
                                    </h5>
                                    <div className='tw-flex tw-items-center tw-gap-2'>
                                        <div className="tw-px-2 tw-py-0.5 tw-rounded-md tw-bg-[#1570EF]/10 dark:tw-bg-white/15 tw-border tw-border-[#B2DDFF] dark:tw-border-white/20 tw-text-[#175CD3] dark:tw-text-white tw-text-xs tw-font-medium tw-inline-flex tw-items-center tw-gap-1">
                                            <IconLayers color="currentColor" />
                                            {selectedRow?.datasets?.summaryText || '4 datasets · 20 partitions'}
                                        </div>
                                        <span className="tw-text-[#717680] dark:tw-text-white/90 tw-text-xs tw-font-medium tw-inline-flex tw-items-center tw-gap-1">
                                            <IconServer /> {truncate15(selectedRow?.environment || 'Production')} • <IconDatabase /> {truncate15(selectedRow?.dataSource || 'Azure Data Lake')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Modal.Header>

                        <Modal.Body className="tw-overflow-x-hidden">
                            <div className="tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-rounded-xl tw-bg-white dark:tw-bg-[#1E1E2D] tw-shadow-sm tw-overflow-hidden">
                                <div className="tw-px-5 tw-py-4 tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-flex tw-items-center tw-justify-between">
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <span className="tw-font-semibold tw-text-[#101828] dark:tw-text-[#FFFFFF] tw-text-sm">Datasets</span>
                                        <span className="tbl-partition-drawer__count">
                                            {selectedRow?.datasets?.list?.length || 0}
                                        </span>
                                    </div>
                                    <div className="tw-w-48 tw-relative tw-flex tw-items-center">
                                        <Search size={14} className="tw-absolute tw-left-2.5 tw-text-gray-400 dark:tw-text-gray-400 tw-pointer-events-none" />
                                        <input
                                            type="text"
                                            className="form-control form-control-sm !tw-pl-8 !tw-text-[13px] !tw-rounded-lg dark:!tw-bg-[#2B2B40] dark:!tw-border-[#363843] dark:!tw-text-white"
                                            placeholder="Search"
                                            value={modalFilterText}
                                            onChange={(e) => setModalFilterText(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="tw-px-4 tw-pt-1 tw-pb-3">
                                    {visibleModalDatasets.length === 0 ? (
                                        renderNoDataPlaceholder("No Data Found")
                                    ) : (
                                        <ul className="tbl-partition-drawer__list">
                                            {visibleModalDatasets.map((ds, idx) => {
                                            const isOpen = expandedDataset === ds.name || modalFilterText.trim().length > 0;
                                            const hasFailedPartition = ds.partitions?.some(p => p.status === 'Failed');
                                            const isDatasetFailed = ds.status === 'Failed' || hasFailedPartition;
                                            const visiblePartitions = ds.partitions;

                                            const renderExclamationBadge = () => (
                                                <sup className="tw-absolute -tw-top-1 -tw-right-1 tw-flex tw-items-center tw-justify-center tw-bg-white dark:tw-bg-[#1E1E2D] tw-rounded-full tw-p-[1px] tw-border tw-border-solid tw-border-white dark:tw-border-[#1E1E2D] tw-z-10 tw-leading-none">
                                                    <IconCircleExclamation size={10} />
                                                </sup>
                                            );

                                            return (
                                                <li key={idx} className={`tbl-partition-drawer__group ${isOpen ? 'is-open' : ''}`}>
                                                    <div className="tbl-partition-drawer__row">
                                                        {ds.hasPartitions ? (
                                                            <button
                                                                type="button"
                                                                className="tbl-partition-drawer__toggle"
                                                                aria-expanded={isOpen}
                                                                aria-label={`Toggle partitions of ${ds.name}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setExpandedDataset(isOpen && modalFilterText.trim().length === 0 ? null : ds.name);
                                                                }}
                                                            >
                                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="m9 18 6-6-6-6" />
                                                                </svg>
                                                            </button>
                                                        ) : (
                                                            <span className="tbl-partition-drawer__toggle" aria-hidden="true" />
                                                        )}

                                                        <button
                                                            type="button"
                                                            className="tbl-partition-drawer__item"
                                                            onClick={() => {
                                                                setSelectedDataset(ds);
                                                                setModalView('dataset_detail');
                                                            }}
                                                        >
                                                            <span className="tw-relative tw-inline-flex tw-items-center">
                                                                <IconLayers color="#1570ef" />
                                                                {isDatasetFailed && renderExclamationBadge()}
                                                            </span>
                                                            <span className="tbl-partition-drawer__name">{ds.name}</span>
                                                        {Number(ds.partitionCount) > 0 && (
                                                            <span className="tbl-partition-drawer__count">
                                                                    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M15 6a9 9 0 0 0-9 9V3" />
                                                                        <circle cx="18" cy="6" r="3" />
                                                                        <circle cx="6" cy="18" r="3" />
                                                                    </svg>
                                                                {ds.partitionCount}
                                                            </span>
                                                        )}
                                                            <span className="tbl-partition-drawer__meta">{ds.status}</span>
                                                        </button>
                                                    </div>

                                                    {ds.hasPartitions && isOpen && (
                                                        <ul className="tbl-partition-drawer__kids">
                                                            {visiblePartitions.map((pt, pIdx) => {
                                                                const isLast = pIdx === visiblePartitions.length - 1;
                                                                const isPartFailed = pt.status === 'Failed';
                                                                return (
                                                                    <li key={pIdx} className={isLast ? 'tbl-partition-drawer__kids-last' : ''}>
                                                                        <button
                                                                            type="button"
                                                                            className={`tbl-partition-drawer__item tbl-partition-drawer__item--child ${isPartFailed ? 'tbl-partition-drawer__item--err' : ''}`}
                                                                            onClick={() => {
                                                                                setSelectedDataset({ name: pt.name, status: pt.status, parent: ds.name, isPartition: true });
                                                                                setModalView('dataset_detail');
                                                                            }}
                                                                        >
                                                                            <svg className="tbl-partition-drawer__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                <path d="M15 6a9 9 0 0 0-9 9V3" />
                                                                                <circle cx="18" cy="6" r="3" />
                                                                                <circle cx="6" cy="18" r="3" />
                                                                            </svg>
                                                                            <span className="tbl-partition-drawer__name">{pt.name}</span>
                                                                            {Number(pt.successfulRefresh) > 0 && (
                                                                                <span className="tbl-partition-drawer__meta">{pt.successfulRefresh}</span>
                                                                            )}
                                                                        </button>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    )}
                                                </li>
                                            );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </Modal.Body>

                        <div className="modal-footer tw-flex tw-items-center tw-justify-between ">
                            {hasFailureSummary && (
                                <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-text-[#D92D20] dark:tw-text-[#F04438]">
                                    <IconFailedStatus size={14} color="#F04438" />
                                    <span>{selectedRow?.summary?.errorsCount || 0} dataset{selectedRow?.summary?.errorsCount === 1 ? '' : 's'} failed</span>
                                </div>
                            )}
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <button
                                    type="button"
                                    className="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-200 tw-bg-white dark:tw-bg-[#2B2B40] tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#363843] tw-rounded-lg hover:tw-bg-gray-50 dark:hover:tw-bg-[#363843] tw-transition-colors"
                                    onClick={() => setShowModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <Modal.Header closeButton>
                            <div className='tw-flex tw-items-center tw-gap-3.5'>
                                    <div className="tw-flex tw-items-center tw-justify-center tw-p-[10px] tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/20 tw-bg-white dark:tw-bg-white/10 tw-rounded-[10px]">
                                        {(selectedDataset?.isPartition || selectedDataset?.parent) ? (
                                            <IconPartition className="tw-text-[#414651] dark:tw-text-white" size={20} />
                                        ) : (
                                            <IconLayers className="tw-text-[#414651] dark:tw-text-white" size={20} />
                                        )}
                                    </div>
                                 <div>
                                     <h5 className="modal-title !tw-m-0 !tw-mb-0.5 tw-text-[15px] tw-font-semibold !tw-text-[#101828] dark:!tw-text-[#FFFFFF]">
                                         {selectedDataset?.name || 'maintenance_work_orders'}
                                     </h5>
                                     <div className="tw-flex tw-items-center tw-gap-2">
                                         {renderModalHeaderStatus(selectedDataset?.status || selectedRow?.profileStatus?.status || 'Completed')}
                                         <span className="tw-text-[#717680] dark:tw-text-white/90 tw-text-xs tw-font-medium tw-inline-flex tw-items-center tw-gap-1">
                                             <IconIntegration /> {selectedRow?.integrationName || 'adl_file_replace'}
                                         </span>
                                     </div>
                                 </div>
                             </div>
                         </Modal.Header>

                         <Modal.Body className="tw-overflow-x-hidden">
                             <button
                                 type="button"
                                    className="tw-text-[#007bff] dark:tw-text-[#007bff] tw-font-semibold tw-text-[13px] tw-border-0 tw-bg-transparent !tw-p-0 tw-inline-flex tw-items-center tw-gap-1 tw-mb-4 tw-cursor-pointer hover:tw-underline"
                                 onClick={() => setModalView('integration')}
                             >
                                 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#007bff" strokeWidth="2.5">
                                     <path d="m15 18-6-6 6-6" />
                                 </svg>
                                 All datasets
                             </button>

                             {(selectedDataset?.isError || selectedDataset?.status === 'Failed' || selectedDataset?.status === 'Error' || (selectedRow?.profileStatus?.status === 'Failed' && (selectedDataset?.name === 'order_list_invoice' || selectedDataset?.name === 'plant_utilisation'))) && (
                                 <div className="alert alert-danger text-left tw-mb-4 tw-flex tw-items-start tw-gap-2.5">
                                     <IconFailedStatus size={16} color="#F04438" className="tw-flex-none tw-mt-0.5" />
                                     <span className="tw-text-sm tw-font-medium tw-leading-relaxed">
                                         {selectedDataset?.errorMessage || "Error while processing the configured dataset. Could be due to unexpected/invalid values in the dataset or incorrect format of the dataset"}
                                     </span>
                                 </div>
                             )}

                             <div className="tw-border tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-rounded-xl tw-bg-white dark:tw-bg-[#1E1E2D] tw-overflow-hidden">
                                 <div className="tw-px-5 tw-py-3.5 tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40]">
                                    <h6 className="!tw-m-0 tw-text-sm tw-font-semibold tw-text-[#101828] dark:tw-text-[#FFFFFF]">
                                        Processing information
                                    </h6>
                                </div>
                                <div className="tw-px-4 tw-py-1">
                                    <div className="tw-grid tw-grid-cols-[180px_1fr] tw-gap-4 tw-px-1 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-items-center">
                                        <span className="tw-text-[#717680] dark:tw-text-[#A1A5B7] tw-text-[13px] tw-font-normal">Last processing time</span>
                                        <span className="tw-text-[#000000] dark:tw-text-[#FFFFFF] tw-text-[13px] tw-font-semibold tw-text-left">
                                            {selectedDataset?.lastProcessingTime || (selectedRow?.integrationName === 'manufacturing_dq' ? "Wed, 12 Aug 2026 09:14:05 GMT" : "Wed, 12 Aug 2026 15:31:13 GMT")}
                                        </span>
                                    </div>
                                    <div className="tw-grid tw-grid-cols-[180px_1fr] tw-gap-4 tw-px-1 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-items-center">
                                        <span className="tw-text-[#717680] dark:tw-text-[#A1A5B7] tw-text-[13px] tw-font-normal">Current status</span>
                                        {renderModalStatusRow(
                                            selectedDataset?.status || (selectedRow?.profileStatus?.status === 'Failed' && (selectedDataset?.name === 'order_list_invoice' || selectedDataset?.name === 'plant_utilisation') ? 'Failed' : selectedRow?.profileStatus?.status || 'Completed'),
                                            selectedDataset?.percentage || ((selectedDataset?.status === 'Failed' || (selectedRow?.profileStatus?.status === 'Failed' && (selectedDataset?.name === 'order_list_invoice' || selectedDataset?.name === 'plant_utilisation'))) ? 100 : selectedRow?.profileStatus?.percentage || 100)
                                        )}
                                    </div>
                                    <div className="tw-grid tw-grid-cols-[180px_1fr] tw-gap-4 tw-px-1 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-items-center">
                                        <span className="tw-text-[#717680] dark:tw-text-[#A1A5B7] tw-text-[13px] tw-font-normal">Successful refresh</span>
                                        <span className="tw-text-[#000000] dark:tw-text-[#FFFFFF] tw-text-[13px] tw-font-semibold tw-text-left">
                                            {selectedDataset?.successfulRefresh !== undefined ? selectedDataset.successfulRefresh : ((selectedDataset?.status === 'Failed' || (selectedRow?.profileStatus?.status === 'Failed' && (selectedDataset?.name === 'order_list_invoice' || selectedDataset?.name === 'plant_utilisation'))) ? 0 : 1)}
                                        </span>
                                    </div>
                                    <div className="tw-grid tw-grid-cols-[180px_1fr] tw-gap-4 tw-px-1 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#E9EAEB] dark:tw-border-[#2B2B40] tw-items-center">
                                        <span className="tw-text-[#717680] dark:tw-text-[#A1A5B7] tw-text-[13px] tw-font-normal">Failed refresh</span>
                                        <span className="tw-text-[#000000] dark:tw-text-[#FFFFFF] tw-text-[13px] tw-font-semibold tw-text-left">
                                            {selectedDataset?.failedRefresh !== undefined ? selectedDataset.failedRefresh : ((selectedDataset?.status === 'Failed' || (selectedRow?.profileStatus?.status === 'Failed' && (selectedDataset?.name === 'order_list_invoice' || selectedDataset?.name === 'plant_utilisation'))) ? 1 : 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Modal.Body>

                        <div className="modal-footer tw-flex tw-items-center tw-justify-end tw-gap-2">
                            <button
                                type="button"
                                className="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-solid tw-border-[#E9EAEB] tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors"
                                onClick={() => setShowModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default ConfigureDataRefreshTable;
